import React, { useState, useEffect } from 'react';
import { inboxFetch } from '../lib/inboxApi';
import { colors } from '../theme/tokens';

function delayLabel(delay, unit) {
  const u = unit === 'seconds' ? (delay === 1 ? 'second' : 'seconds')
    : unit === 'minutes' ? (delay === 1 ? 'minute' : 'minutes')
    : delay === 1 ? 'hour' : 'hours';
  return `Sends ${delay} ${u} after first contact`;
}

export default function InboxAutoReply() {
  const [enabled, setEnabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await inboxFetch(`/api/settings/auto-reply`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setEnabled(data.enabled || false);
      setMessages(data.messages || []);
    } catch {
      setError('Could not load settings.');
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = (index, field, value) => {
    setMessages(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await inboxFetch(`/api/settings/auto-reply`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, messages })
      });
      if (!res.ok) throw new Error('Failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.loading}>Loading settings…</div>;

  return (
    <div style={s.container}>
      <h2 style={s.title}>Auto-Reply Settings</h2>
      <p style={s.description}>
        When enabled, these messages are sent automatically to new contacts in sequence.
        Only triggers once per new conversation — not on every message.
      </p>

      {error && <div style={s.error}>{error}</div>}

      {/* Enable / Disable toggle */}
      <div style={s.toggleRow}>
        <span style={s.toggleRowLabel}>Auto-reply sequence</span>
        <button
          style={{ ...s.toggle, ...(enabled ? s.toggleOn : {}) }}
          onClick={() => setEnabled(v => !v)}
        >
          <span style={{ ...s.togglePill, ...(enabled ? s.togglePillOn : {}) }}>
            <span style={{ ...s.toggleThumb, ...(enabled ? s.toggleThumbOn : {}) }} />
          </span>
          <span style={s.toggleText}>{enabled ? 'Enabled' : 'Disabled'}</span>
        </button>
      </div>

      {/* Message cards */}
      <div style={s.cards}>
        {messages.map((msg, i) => (
          <div key={i} style={{ ...s.card, ...(enabled ? {} : s.cardDisabled) }}>
            <div style={s.cardHeader}>
              <span style={s.stepBadge}>Step {i + 1}</span>
              <span style={s.delayHint}>{delayLabel(msg.delay, msg.unit)}</span>
            </div>

            <div style={s.delayRow}>
              <div style={s.delayField}>
                <label style={s.fieldLabel}>Delay</label>
                <input
                  type="number"
                  min="0"
                  style={s.delayInput}
                  value={msg.delay}
                  onChange={e => updateMessage(i, 'delay', Math.max(0, parseInt(e.target.value) || 0))}
                  disabled={!enabled}
                />
              </div>
              <div style={s.unitField}>
                <label style={s.fieldLabel}>Unit</label>
                <select
                  style={s.unitSelect}
                  value={msg.unit}
                  onChange={e => updateMessage(i, 'unit', e.target.value)}
                  disabled={!enabled}
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
            </div>

            <div style={s.msgField}>
              <label style={s.fieldLabel}>Message</label>
              <textarea
                style={s.textarea}
                rows={3}
                value={msg.message}
                onChange={e => updateMessage(i, 'message', e.target.value)}
                placeholder="Enter the message to send…"
                disabled={!enabled}
              />
              <div style={s.charCount}>{msg.message.length} characters</div>
            </div>

            {/* Skip-if-agent-replied toggle */}
            <label
              style={{
                ...s.skipRow,
                ...(enabled ? {} : { opacity: 0.6, cursor: 'not-allowed' })
              }}
              title="If a salesperson replies to the customer before this step fires, it won't be sent."
            >
              <input
                type="checkbox"
                checked={!!msg.skipIfAgentReplied}
                onChange={e => updateMessage(i, 'skipIfAgentReplied', e.target.checked)}
                disabled={!enabled}
                style={s.skipCheckbox}
              />
              <div style={s.skipTextBlock}>
                <span style={s.skipTitle}>Skip if an agent has already replied</span>
                <span style={s.skipHint}>
                  Won't send this step if a salesperson has replied in the meantime.
                </span>
              </div>
            </label>
          </div>
        ))}
      </div>

      <div style={s.footer}>
        <button
          style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Changes'}
        </button>
        {saved && <span style={s.savedMsg}>All changes saved.</span>}
      </div>
    </div>
  );
}

const s = {
  container: {
    padding: '24px',
    maxWidth: '600px',
    overflowY: 'auto'
  },
  loading: {
    padding: '40px',
    color: colors.textMuted,
    textAlign: 'center',
    fontSize: '14px'
  },
  title: {
    margin: '0 0 6px',
    fontSize: '20px',
    fontWeight: '700',
    color: colors.text
  },
  description: {
    fontSize: '13px',
    color: colors.textMuted,
    marginBottom: '20px',
    lineHeight: '1.6'
  },
  error: {
    background: 'rgba(229,84,75,0.14)',
    color: '#f3b0aa',
    border: '1px solid rgba(229,84,75,0.35)',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: colors.glass,
    borderRadius: '10px',
    border: `1px solid ${colors.glassBorder}`,
    marginBottom: '20px'
  },
  toggleRowLabel: {
    fontSize: '14px',
    fontWeight: '700',
    color: colors.text
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '20px',
    background: colors.surfaceDeep,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: colors.textMuted,
    transition: 'all 0.2s'
  },
  toggleOn: {
    background: 'rgba(47,182,92,0.18)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: colors.parGreenBright
  },
  togglePill: {
    width: '36px',
    height: '20px',
    background: colors.borderStrong,
    borderRadius: '10px',
    position: 'relative',
    flexShrink: 0,
    transition: 'background 0.2s'
  },
  togglePillOn: {
    background: '#2fb65c'
  },
  toggleThumb: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '16px',
    height: '16px',
    background: 'white',
    borderRadius: '50%',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  },
  toggleThumbOn: {
    left: '18px'
  },
  toggleText: {
    userSelect: 'none',
    minWidth: '56px'
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  card: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '16px',
    transition: 'opacity 0.2s'
  },
  cardDisabled: {
    opacity: 0.5
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px'
  },
  stepBadge: {
    background: 'rgba(47,182,92,0.20)',
    color: colors.parGreenBright,
    border: '1px solid rgba(47,182,92,0.45)',
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '10px'
  },
  delayHint: {
    fontSize: '12px',
    color: colors.textMuted,
    fontStyle: 'italic'
  },
  delayRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  },
  delayField: {
    flex: '0 0 100px'
  },
  unitField: {
    flex: '0 0 130px'
  },
  fieldLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '5px'
  },
  delayInput: {
    width: '100%',
    padding: '7px 10px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    outline: 'none',
    boxSizing: 'border-box',
    background: colors.inputBg,
    color: colors.text
  },
  unitSelect: {
    width: '100%',
    padding: '7px 10px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    background: colors.surfaceDeep,
    color: colors.text,
    cursor: 'pointer',
    boxSizing: 'border-box'
  },
  msgField: {
    marginBottom: '0'
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    background: colors.inputBg,
    color: colors.text
  },
  charCount: {
    fontSize: '11px',
    color: colors.textFaint,
    textAlign: 'right',
    marginTop: '3px'
  },
  skipRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginTop: '12px',
    padding: '10px 12px',
    background: colors.surfaceSoft,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '8px',
    cursor: 'pointer'
  },
  skipCheckbox: {
    marginTop: '2px',
    width: '16px',
    height: '16px',
    accentColor: '#2fb65c',
    cursor: 'pointer',
    flexShrink: 0
  },
  skipTextBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    lineHeight: 1.4
  },
  skipTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: colors.text
  },
  skipHint: {
    fontSize: '11px',
    color: colors.textMuted
  },
  footer: {
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  saveBtn: {
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 28px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveBtnDisabled: {
    background: colors.surface2,
    color: colors.textFaint,
    cursor: 'not-allowed'
  },
  savedMsg: {
    color: colors.parGreenBright,
    fontSize: '13px',
    fontWeight: '600'
  }
};

